import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './calendar.css';
import { DatePickerProps, IsoDateString } from '../../../types/DesignSystem/DatePicker';
import FieldTitle from '../FieldTitle/FieldTitle';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import { Calendar } from '../Iconography/Icons';
import { CONTROL_HEIGHT, FIELD_INSET } from '../controlSize';
import { FIELD_BOX, fieldBorder, FIELD_REST, FIELD_DISABLED } from '../fieldChrome';
import { useControlSize } from '../DensityProvider/DensityProvider';

/**
 * Format a `Date` as `YYYY-MM-DD` using its LOCAL calendar day.
 *
 * Not `toISOString().slice(0, 10)`, which converts to UTC first: a date picked at
 * 21:00 in New York is already the next day in UTC, so that one-liner silently
 * returns tomorrow. Every hand-rolled copy of this formatter in the app got this
 * right by building the string from the local getters; the shortcut is the trap.
 */
export const toIsoDate = (date: Date): IsoDateString => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse whatever a caller passed as `value` into a `Date`, or `null`.
 *
 * A bare `YYYY-MM-DD` is parsed FIELD BY FIELD rather than through `new Date(str)`.
 * The spec says a date-only string is UTC, so `new Date('2026-08-29')` is UTC
 * midnight — which is 2026-08-**28** 20:00 in New York, and the calendar therefore
 * highlighted the wrong day for every user west of Greenwich. Anything else (a real
 * `Date`, a full timestamp) is passed through untouched.
 */
const parseValue = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parts = value.match(ISO_DATE_ONLY);
  if (parts) {
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Render a `Date` in the field's display format — used only by the disabled branch,
 * which has no react-datepicker to do it. Supports the format tokens this app
 * actually passes (`dd-MM-yyyy`, `yyyy-MM-dd`); anything else falls back to ISO
 * rather than rendering a half-substituted string.
 */
const formatForDisplay = (date: Date, dateFormat: string): string => {
  const iso = toIsoDate(date);
  if (dateFormat === 'dd-MM-yyyy') {
    const [year, month, day] = iso.split('-');
    return `${day}-${month}-${year}`;
  }
  return iso;
};

/**
 * The element react-datepicker clones as its trigger.
 *
 * It has to be a component rather than a bare `<input>` because the library injects
 * its own `value`, `onChange`, `onClick`, `onFocus` and `onKeyDown` through
 * `cloneElement`, and cloneElement's config WINS on every key it carries — including
 * keys whose value is `undefined`. Handing it a raw element would let the library's
 * `className: undefined` wipe the field chrome. Forwarding the merged props from
 * inside means the library's behaviour and our styling both survive.
 */
const DsDateInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function DsDateInput(props, inputRef) {
    return <input {...props} ref={inputRef} />;
  },
);

/**
 * The design system's date field.
 *
 * Built ON react-datepicker@9 rather than from scratch — the app already depends on
 * it at nine call sites, and a hand-rolled calendar would have to re-earn keyboard
 * grid navigation, month rollover and locale week starts. What this component owns is
 * everything AROUND the grid: the field chrome, the label association, the palette
 * (`calendar.css`) and the two library footguns below.
 *
 * ---
 *
 * **Footgun 1 — `disabled` is poison while `open` is controlled, and this component
 * makes that unreachable.**
 *
 * All three of the connector's end-date pickers passed `disabled={true}` alongside an
 * explicit `open`. Under react-datepicker@3.6.0 that was a no-op: `disabled` gated
 * only the input element and the calendar's default open-state, both bypassed once
 * `open` is controlled. Under 9.1.0 — pulled in by the React 19 upgrade —
 * `isDayDisabled` short-circuits `true` whenever the top-level `disabled` prop is
 * set, **before it even looks at `minDate`**. Every day cell went dead on gms-dev
 * while gms-qa kept working, with no type error and no server signal.
 * (`okf/connector/e2e.md` § 7 records the incident.)
 *
 * The fix there was "remember not to pass it". That is not a fix, it is a note. Here
 * the prop cannot reach the library at all: a `disabled` DatePicker renders its
 * trigger inert and **does not mount the calendar**, so there is no grid to
 * accidentally disable. The invariant is the component's, not the caller's.
 *
 * **Footgun 2 — the popper's default strategy is `absolute`.**
 *
 * 9.x moved to `@floating-ui`, whose default positions against the nearest scrolling
 * ancestor. In a drawer or modal — which is where every date field in this app lives
 * — that ancestor is the panel, so the calendar was clipped by it. `strategy: 'fixed'`
 * positions against the viewport instead. It is applied unconditionally here rather
 * than offered as a prop: there is no call site that wants the clipped one.
 *
 * ---
 *
 * **`onChange` hands back the ISO string as well as the `Date`.** Five files in this
 * app carried their own zero-padding `formatEndDate`; two were byte-identical, and
 * one was commented "format the date into dd-mm-yyyy" while returning `yyyy-mm-dd`.
 * The string the component emits is the one it displays a formatted version of, so
 * the two cannot drift.
 */
const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker({
  label,
  value: valueProp,
  defaultValue,
  onChange,
  placeholder = 'dd-mm-yyyy',
  minDate,
  maxDate,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  dateFormat = 'dd-MM-yyyy',
  open,
  onOpenChange,
  size: sizeProp,
  labelSize,
  className = '',
  id,
  'data-testid': dataTestId,
  'aria-describedby': describedByProp,
  ...rest
}, ref) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  const uid = React.useId();
  const inputId = id ?? `${uid}-date`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  /* § 356 / § 357 — see TextField. The legacy default, `value = null`, is the fallback. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState<Date | IsoDateString | null>(valueProp, defaultValue, null);

  const describedBy = [describedByProp, ...messageIds({ helperId, errorId }, { helperText, errorMessage })]
    .filter(Boolean)
    .join(' ') || undefined;

  const selected = parseValue(value);

  const handleChange = (date: Date | null) => {
    setValue(date);
    onChange?.(date, date ? toIsoDate(date) : '');
  };

  const fieldClassName = `tw-w-full ${CONTROL_HEIGHT[size]} ${FIELD_INSET[size]} tw-pr-9 ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled })} tw-text-body-primary tw-text-ink placeholder:tw-text-ink-secondary ${disabled ? FIELD_DISABLED : FIELD_REST}`;

  return (
    <div className={className}>
      {label && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it stays the input's accessible name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          disabled={disabled}
          htmlFor={inputId}
        />
      )}

      <div className="tw-relative">
        {/*
          A disabled field mounts NO calendar — see "Footgun 1" above. Returning a
          plain input also means a disabled DatePicker cannot be opened by any route:
          not by the `open` prop, not by focus, not by a click.
        */}
        {disabled ? (
          <input
            {...rest}
            ref={ref}
            id={inputId}
            data-testid={dataTestId}
            type="text"
            disabled
            readOnly
            value={selected ? formatForDisplay(selected, dateFormat) : ''}
            placeholder={placeholder}
            aria-describedby={describedBy}
            className={fieldClassName}
          />
        ) : (
          <ReactDatePicker
            id={inputId}
            selected={selected}
            onChange={handleChange}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={dateFormat}
            placeholderText={placeholder}
            /* Controlled only when the caller opted in; `undefined` leaves the
               library's own focus/click handling alone. */
            open={open}
            onCalendarOpen={onOpenChange ? () => onOpenChange(true) : undefined}
            onCalendarClose={onOpenChange ? () => onOpenChange(false) : undefined}
            showPopperArrow={false}
            popperProps={{ strategy: 'fixed' }}
            calendarClassName="ds-calendar"
            wrapperClassName="tw-w-full tw-block"
            /* On ReactDatePicker, not on `customInput` — the library injects it into
               the clone, and anything set only on the clone loses to that injection. */
            className={fieldClassName}
            customInput={
              <DsDateInput
                {...rest}
                ref={ref}
                data-testid={dataTestId}
                aria-invalid={invalid || undefined}
                aria-required={required || undefined}
                aria-describedby={describedBy}
              />
            }
          />
        )}

        {/*
          The affordance. `pointer-events-none` so it never intercepts a click meant
          for the field — the legacy calendar glyph sat inside an `input-group` span
          that swallowed clicks on its half of the control, so the only way to open
          the picker was to hit the text. `aria-hidden` because the field is already
          named by its label; announcing "calendar" again adds nothing.
        */}
        <span
          aria-hidden="true"
          className={`tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 ${
            disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'
          }`}
        >
          <Calendar size={16} />
        </span>
      </div>

      {/* § 302 — the one helper seam (`fieldHelper.tsx`). */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />
    </div>
  );
});

export default DatePicker;
